import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react'
import * as TabsPrimitive from '@radix-ui/react-tabs'
import { cn } from '@/lib/utils'

/**
 * Tabs — примитив вкладок (F2-04, design-tokens/component-catalog): до этой задачи в ДС не было
 * ни одного компонента вкладок. На Radix (`@radix-ui/react-tabs`) — верная семантика ARIA
 * (`tablist`/`tab`/`tabpanel`), обход стрелками и активация по `Enter`/`Space` из коробки.
 * Composition в стиле уже принятого в ДС паттерна (см. select.tsx, dialog.tsx):
 * `Tabs` (Root) / `TabsList` / `TabsTrigger` / `TabsContent`.
 */
export const Tabs = TabsPrimitive.Root

export const TabsList = forwardRef<
  ElementRef<typeof TabsPrimitive.List>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn('flex gap-1 border-b border-line', className)}
    {...props}
  />
))
TabsList.displayName = 'TabsList'

export const TabsTrigger = forwardRef<
  ElementRef<typeof TabsPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      '-mb-px border-b-2 border-transparent px-3 py-2 text-14 font-medium text-txt-secondary outline-none transition-colors',
      'hover:text-txt',
      'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent',
      'data-[state=active]:border-accent data-[state=active]:text-accent-txt',
      'disabled:cursor-not-allowed disabled:opacity-50',
      className,
    )}
    {...props}
  />
))
TabsTrigger.displayName = 'TabsTrigger'

export const TabsContent = forwardRef<
  ElementRef<typeof TabsPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content ref={ref} className={cn('pt-4 outline-none', className)} {...props} />
))
TabsContent.displayName = 'TabsContent'
